import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, RefreshCw, Download } from "lucide-react";
import ClientCard from "../components/ClientCard";
import EmptyState from "../components/EmptyState";
import LoadingSkeleton from "../components/LoadingSkeleton";
import Button from "../components/Button";
import type { Client } from "../types";

export default function ClientsPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const response = await fetch("/api/clients");
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error("Error loading clients:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const syncFromSparkOS = async () => {
    setIsSyncing(true);
    setSyncMessage("");
    try {
      const response = await fetch("/api/clients/sync-spark-os", {
        method: "POST",
      });
      const data = await response.json();
      if (response.ok) {
        setSyncMessage(`Synced: ${data.results.added} added, ${data.results.skipped} already existed`);
        loadClients();
      } else {
        setSyncMessage(data.error || "Sync failed");
      }
    } catch (error) {
      setSyncMessage("Failed to sync from Spark OS");
    } finally {
      setIsSyncing(false);
      setTimeout(() => setSyncMessage(""), 5000);
    }
  };

  const filteredClients = clients.filter((client) => {
    const name = client.name.toLowerCase();
    const company = (client.company || "").toLowerCase();
    const query = searchQuery.toLowerCase();
    return name.includes(query) || company.includes(query);
  });

  if (isLoading) {
    return <LoadingSkeleton count={4} />;
  }

  if (clients.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-slate-900">Clients</h2>
          <Button 
            variant="outline"
            size="sm"
            onClick={syncFromSparkOS}
            loading={isSyncing}
            title="Sync from Spark OS"
          >
            <Download className="w-4 h-4" />
          </Button>
        </div>
        {syncMessage && (
          <div className="bg-teal-50 text-teal-700 px-4 py-3 rounded-lg text-sm">
            {syncMessage}
          </div>
        )}
        <EmptyState
          icon="users"
          title="No clients yet"
          description="Sync clients from Spark OS or add your first client manually."
          actionLabel="Add Client Manually"
          onAction={() => navigate("/clients/new")}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-slate-900">Clients</h2>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={syncFromSparkOS}
            loading={isSyncing}
            title="Sync from Spark OS"
          >
            <Download className="w-4 h-4" />
          </Button>
          <Button size="sm" onClick={() => navigate("/clients/new")}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {syncMessage && (
        <div className="bg-teal-50 text-teal-700 px-4 py-3 rounded-lg text-sm">
          {syncMessage}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          type="text"
          placeholder="Search clients..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-12 pr-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
        />
      </div>

      {filteredClients.length === 0 ? (
        <p className="text-center text-slate-500 py-8">No clients match your search</p>
      ) : (
        <div className="space-y-3">
          {filteredClients.map((client) => (
            <ClientCard
              key={client.id}
              client={client}
              onClick={() => navigate(`/clients/${client.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
